//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-scheduler-room-input imports
import { eventSchedulerRoomInputFragments } from "./EventSchedulerRoomInput.gql";
import * as s from "./EventSchedulerRoomInput.styles";
import { IContainer, IProps, IState } from "./EventSchedulerRoomInputTypes";
// other imports
import { CalloutBox } from "#webapp/src/components/callout-box";
import { EventSchedulerSuggestedRoom } from "#webapp/src/components/event-scheduler-suggested-room";
import { MoreButton } from "#webapp/src/components/more-button";
import { Button, TextField } from "@clockwise/design-system";
// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// material-ui
import { H2 } from "#webapp/src/components/ui";
import { AddCircle, Business, Close } from "@clockwise/design-system/icons";
import { IconButton } from "@material-ui/core";
// styles
import { EcosystemEnum } from "@clockwise/schema";
import { webappDefaultWindowName } from "@clockwise/web-commons/src/constants/route.constants";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { EcosystemContext } from "@clockwise/web-commons/src/util/ecosystem";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import pluralize from "pluralize";

//////////////////
// COMPONENT
//////////////////
class EventSchedulerRoomInputCmpt extends React.Component<IProps, IState> {
  static contextType = EcosystemContext;
  declare context: React.ContextType<typeof EcosystemContext>;

  constructor(props: IProps) {
    super(props);
    this.state = {
      ...this.getFilteredRooms("", props.roomIds || [], props.workingGroup, props.selectedSlot),
      editMode: !!props.open,
      selectedRoomIds: props.roomIds || [],
      roomFilterInput: "",
      showUnavailableRooms: false,
      showSelectedRoomCountWarning: (props.roomIds || []).length > 10,
    };
  }

  public componentDidMount() {
    if (this.state.filteredAvailable.length === 0 || !this.props.selectedSlot) {
      return;
    } else if (this.context === EcosystemEnum.Google && !this.props.useAutoSchedule) {
      return;
    }

    // auto-select
    this.setState({ selectedRoomIds: [this.state.filteredAvailable[0].roomId] }, () =>
      this.maybeRoomCallback(),
    );
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const { roomIds, workingGroup, selectedSlot, open } = nextProps;
    const selectedRoomIds = roomIds || [];
    const { filteredAvailable, filteredUnavailable } = this.getFilteredRooms(
      "",
      selectedRoomIds,
      workingGroup,
      selectedSlot,
    );

    this.setState({
      filteredAvailable,
      filteredUnavailable,
      selectedRoomIds,
    });

    if (open && !this.props.open) {
      this.expand(true);
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private expand = (expand: boolean) => {
    const trackingEvent = expand
      ? TrackingEvents.SCHEDULING.EVENT_SCHEDULER_ROOM_INPUT_EXPANDED
      : TrackingEvents.SCHEDULING.EVENT_SCHEDULER_ROOM_INPUT_CLOSED;
    track(trackingEvent, {
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics
    this.setState({ editMode: expand });
    if (this.props.onEditModeChange) {
      this.props.onEditModeChange(expand);
    }
  };

  private getFilteredRooms = (
    filterQuery: string,
    selectedRoomIds: string[],
    workingGroup: ISchema.IWorkingGroup | null,
    selectedSlot?: ISchema.ISchedulingSlot,
  ) => {
    const roomImpacts = (selectedSlot && selectedSlot.roomImpacts) || [];

    // get available/unavaiable roomIds from impacts
    const unavailableRoomIds = roomImpacts
      .filter((impact) => !impact.available)
      .map((impact) => impact.roomId);
    const availableRoomIds = roomImpacts
      .filter((impact) => impact.available)
      .map((impact) => impact.roomId);

    // get available/unavaiable rooms from impacts
    const unavailableRooms: ISchema.IRoom[] = [];
    unavailableRoomIds.forEach((roomId) => {
      const room = workingGroup && workingGroup.rooms.find((r) => r.roomId === roomId);
      if (room) {
        unavailableRooms.push(room);
      }
    });

    const availableRooms: ISchema.IRoom[] = [];
    availableRoomIds.forEach((roomId) => {
      const room = workingGroup && workingGroup.rooms.find((r) => r.roomId === roomId);
      if (room) {
        availableRooms.push(room);
      }
    });

    // get selected available/unavailable rooms from selectedIds
    const unselectedUnavailableRooms: ISchema.IRoom[] = unavailableRooms.filter(
      (room) => selectedRoomIds.findIndex((roomId) => roomId === room.roomId) === -1,
    );
    const unselectedAvailableRooms: ISchema.IRoom[] = availableRooms.filter(
      (room) => selectedRoomIds.findIndex((roomId) => roomId === room.roomId) === -1,
    );

    if (!filterQuery) {
      // if no filter query, just return unselected rooms
      return {
        filteredUnavailable: unselectedUnavailableRooms,
        filteredAvailable: unselectedAvailableRooms,
      };
    }

    return {
      // otherwise filter
      filteredUnavailable: unselectedUnavailableRooms.filter(
        (room) =>
          room && room.name && room.name.toLowerCase().indexOf(filterQuery.toLowerCase()) > -1,
      ),
      filteredAvailable: unselectedAvailableRooms.filter(
        (room) =>
          room && room.name && room.name.toLowerCase().indexOf(filterQuery.toLowerCase()) > -1,
      ),
    };
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  private maybeRoomCallback = () => {
    if (this.props.onChange) {
      this.props.onChange(this.state.selectedRoomIds);
    }
  };

  private onRoomSelection = (room: ISchema.IRoom) => {
    const { workingGroup, selectedSlot } = this.props;
    const selectedRoomIds = [...this.state.selectedRoomIds, room.roomId];

    if (selectedRoomIds.length > 10) {
      return this.setState({ showSelectedRoomCountWarning: true });
    }

    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_ROOM_INPUT_ROOM_SELECTED, {
      roomId: room.roomId,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics

    this.setState(
      {
        ...this.getFilteredRooms(
          this.state.roomFilterInput,
          selectedRoomIds || [],
          workingGroup,
          selectedSlot,
        ),
        selectedRoomIds,
        showSelectedRoomCountWarning: selectedRoomIds.length > 10,
      },
      () => this.maybeRoomCallback(),
    );
  };

  private onRoomRemoval = (room: ISchema.IRoom) => {
    const { workingGroup, selectedSlot } = this.props;
    const { roomFilterInput } = this.state;
    const selectedRoomIds = this.state.selectedRoomIds.filter((roomId) => room.roomId !== roomId);

    track(TrackingEvents.SCHEDULING.EVENT_SCHEDULER_ROOM_INPUT_ROOM_REMOVED, {
      roomId: room.roomId,
      schedulingSessionId: this.props.schedulingSessionId,
    }); // analytics

    this.setState(
      {
        ...this.getFilteredRooms(
          roomFilterInput,
          selectedRoomIds || [],
          workingGroup,
          selectedSlot,
        ),
        selectedRoomIds,
        showSelectedRoomCountWarning: selectedRoomIds.length > 10,
      },
      () => this.maybeRoomCallback(),
    );
  };

  private onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const roomFilterInput = event.target.value;
    const { workingGroup, selectedSlot } = this.props;
    const { selectedRoomIds } = this.state;
    this.setState({
      ...this.getFilteredRooms(roomFilterInput, selectedRoomIds, workingGroup, selectedSlot),
      roomFilterInput,
    });
  };

  private onToggleUnavailableRooms = () => {
    this.setState({ showUnavailableRooms: !this.state.showUnavailableRooms });
  };

  private openRoomPreferencesSettings = () => {
    window.open(getUrlForFeature("preferences"), webappDefaultWindowName);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderCalloutBox = (message: string, isWarning?: boolean) => {
    return (
      <CalloutBox info={!isWarning} warning={isWarning}>
        {message}
      </CalloutBox>
    );
  };

  private renderRooms = (
    rooms: ISchema.IRoom[],
    selectedSlot?: ISchema.ISchedulingSlot,
    onAdd?: (room: ISchema.IRoom) => void,
    onRemove?: (room: ISchema.IRoom) => void,
  ) => {
    if (rooms.length < 1) {
      return this.renderCalloutBox(
        "None of your preferred rooms are available at this selected time.",
      );
    }

    return rooms.map((room) => {
      return (
        <EventSchedulerSuggestedRoom
          key={room.roomId}
          room={room}
          schedulingRoomImpact={((selectedSlot && selectedSlot.roomImpacts) || []).find(
            (roomImpact) => roomImpact.roomId === room.roomId,
          )}
          onAdd={onAdd}
          onRemove={onRemove}
          selected={!!onRemove} // assume it's selected if onRemove exists
        />
      );
    });
  };

  private renderRoomSection = (
    title: string,
    rooms: ISchema.IRoom[],
    selectedSlot?: ISchema.ISchedulingSlot,
    onAdd?: (room: ISchema.IRoom) => void,
    onRemove?: (room: ISchema.IRoom) => void,
  ) => {
    return (
      <div style={{ marginTop: 20 }}>
        <H2 style={s.sectionHeader}>{title}</H2>
        {this.renderRooms(rooms, selectedSlot, onAdd, onRemove)}
      </div>
    );
  };

  private maybeRenderMoreToggle = (
    filteredUnavailable: ISchema.IRoom[],
    showUnavailableRooms: boolean,
  ) => {
    if (!filteredUnavailable.length) {
      return <span />;
    }

    return (
      <div style={s.moreButton}>
        <MoreButton
          expanded={this.state.showUnavailableRooms}
          onClick={() => this.onToggleUnavailableRooms()}
          label={`${showUnavailableRooms ? "Hide" : "View"} unavailable rooms`}
        />
      </div>
    );
  };

  private renderPreferredRoomButton = () => {
    return (
      <div
        onClick={() => this.openRoomPreferencesSettings()}
        style={{ display: "flex", alignItems: "center", margin: "20px 0 0 0", cursor: "pointer" }}
      >
        <AddCircle style={{ color: greys.blueGrey, marginRight: 10 }} />
        <div style={{ fontSize: 14, color: greys.slate }}>Add preferred room</div>
      </div>
    );
  };

  private renderViewMode = (workingGroup: ISchema.IWorkingGroup) => {
    const { selectedSlot } = this.props;
    const { selectedRoomIds, filteredAvailable } = this.state;

    const selectedRooms = workingGroup.rooms.filter((room) => {
      return selectedRoomIds.findIndex((roomId) => roomId === room.roomId) !== -1;
    });

    if (selectedRooms.length > 0) {
      return (
        <div style={s.viewModeContainer}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Business {...s.businessIcon} />
            <div style={{ width: "100%" }}>
              {this.renderRooms(selectedRooms, selectedSlot, undefined, this.onRoomRemoval)}
            </div>
          </div>
          {this.context !== EcosystemEnum.Microsoft && (
            <Button {...s.viewAllBtn} onClick={() => this.expand(true)}>
              {selectedRoomIds.length > 0 ? "Add another room" : "View all rooms"}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div style={s.viewModeContainer}>
        <div>
          <H2 style={s.sectionHeader}>Choose a room</H2>
          {this.renderRooms(filteredAvailable.slice(0, 3), selectedSlot, this.onRoomSelection)}
        </div>
        {this.context !== EcosystemEnum.Microsoft && filteredAvailable.length > 0 ? (
          <Button {...s.viewAllBtn} onClick={() => this.expand(true)}>
            {selectedRoomIds.length > 0 ? "Add another room" : "View all rooms"}
          </Button>
        ) : this.context !== EcosystemEnum.Microsoft ? (
          this.renderPreferredRoomButton()
        ) : (
          ""
        )}
      </div>
    );
  };

  private renderEditMode = (workingGroup: ISchema.IWorkingGroup) => {
    const { selectedSlot } = this.props;
    const {
      selectedRoomIds,
      filteredAvailable,
      filteredUnavailable,
      showUnavailableRooms,
      roomFilterInput,
    } = this.state;

    const selectedRooms = workingGroup.rooms.filter((room) => {
      return selectedRoomIds.findIndex((roomId) => roomId === room.roomId) !== -1;
    });

    return (
      <div style={s.editModeContainer}>
        <div style={s.header}>
          <IconButton style={{ marginLeft: -10 }} onClick={() => this.expand(false)}>
            <Close {...s.closeIcon} />
          </IconButton>
          <H2 style={s.title}>Choose a room</H2>
        </div>
        <div style={s.searchContainer}>
          <TextField
            label="Search for rooms"
            fullWidth
            value={roomFilterInput}
            onChange={this.onFilterChange}
          />
        </div>
        {this.state.showSelectedRoomCountWarning
          ? this.renderCalloutBox(`Meetings can't have more than 10 rooms selected.`, true)
          : ""}
        {selectedRooms.length > 0
          ? this.renderRoomSection(
              `Currently selected ${pluralize("room", selectedRooms.length)}`,
              selectedRooms,
              selectedSlot,
              undefined,
              this.onRoomRemoval,
            )
          : ""}
        {this.renderRoomSection(
          "Available rooms",
          filteredAvailable,
          selectedSlot,
          this.onRoomSelection,
        )}
        {this.maybeRenderMoreToggle(filteredUnavailable, showUnavailableRooms)}
        {showUnavailableRooms && filteredUnavailable.length
          ? this.renderRooms(filteredUnavailable, selectedSlot, this.onRoomSelection)
          : ""}
        {this.renderPreferredRoomButton()}
        <div className="cw-flex cw-flex-col cw-gap-2" style={s.buttonContainer}>
          <Button
            sentiment="positive"
            onClick={() => this.expand(false)}
            fullWidth
            cw-id="event-scheduler-room-input-done"
          >
            Done
          </Button>
        </div>
      </div>
    );
  };

  public render() {
    const { workingGroup, schedulingResponseErrorable } = this.props;
    const { editMode } = this.state;

    if (schedulingResponseErrorable.__typename === "GraphEntityError") {
      logger.error("Graph entity error with schedulingResponseErrorable :(");
      return <span />;
    } else if (!workingGroup) {
      return <span />;
    }

    return (
      <div>
        {editMode && this.renderEditMode(workingGroup)}
        {this.renderViewMode(workingGroup)}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
  };
}

const EventSchedulerRoomInputRedux = connect(mapStateToProps, {})(EventSchedulerRoomInputCmpt);

//////////////////
// RELAY
//////////////////
export const EventSchedulerRoomInput = createFragmentContainer<IContainer>(
  EventSchedulerRoomInputRedux,
  eventSchedulerRoomInputFragments,
);
